import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CSS_PRIMARY } from '../../../constants/style';

/**
 * Search — Premium reusable search input.
 *
 * Props:
 *   onSearch(val)   — fired (debounced) on every keystroke
 *   width           — CSS width, e.g. "320px", "100%"
 *   height          — CSS height, e.g. "40px", "2.5rem" (default: auto)
 *   placeholder     — input placeholder
 *   debounce        — delay in ms (default 300)
 *   isLoading       — shows spinning arc instead of search icon
 *   initialValue    — pre-fill value
 *   onClear()       — called when ✕ is clicked
 *   id              — HTML id for the <input>
 */
const Search = ({
  onSearch,
  width = '300px',
  height, // e.g. "40px", "2.5rem" — if omitted, height is automatic
  placeholder = 'Search...',
  debounce = 300,
  isLoading = false,
  initialValue = '',
  onClear,
  id = 'search-input'
}) => {
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const debounceTimer = useRef(null);
  const inputRef = useRef(null);

  const fireSearch = useCallback(
    (val) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        if (onSearch) onSearch(val);
      }, debounce);
    },
    [onSearch, debounce]
  );

  const handleChange = (e) => {
    const val = e.target.value;
    setValue(val);
    fireSearch(val);
  };

  const handleClear = () => {
    setValue('');
    if (onSearch) onSearch('');
    if (onClear) onClear();
    if (inputRef.current) inputRef.current.focus();
  };

  // Keyboard shortcut: Ctrl+K / Cmd+K focuses search
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape' && isFocused) {
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isFocused]);

  useEffect(() => () => debounceTimer.current && clearTimeout(debounceTimer.current), []);

  // inject keyframes once
  if (typeof document !== 'undefined' && !document.getElementById('__search_styles__')) {
    const s = document.createElement('style');
    s.id = '__search_styles__';
    s.innerHTML = `
      @keyframes __spin { to { transform: rotate(360deg); } }
      @keyframes __pulse-ring {
        0%   { box-shadow: 0 0 0 0 rgba(var(--primary-rgb,46,213,115), 0.35); }
        70%  { box-shadow: 0 0 0 7px rgba(var(--primary-rgb,46,213,115), 0); }
        100% { box-shadow: 0 0 0 0 rgba(var(--primary-rgb,46,213,115), 0); }
      }
      .__search-input::placeholder { color:#b0bec5; font-size:0.84rem; letter-spacing:0.01em; }
      .__search-input:focus { outline:none; }
    `;
    document.head.appendChild(s);
  }

  const primaryColor = `var(--primary, ${CSS_PRIMARY})`;

  return (
    <div style={{ width, display: 'inline-flex', flexDirection: 'column', position: 'relative' }}>
      {/* ── Card shell ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 14px',
          borderRadius: '12px',
          background: isFocused ? '#fff' : '#f4f6f8',
          border: isFocused ? `1.8px solid ${primaryColor}` : '1.8px solid #e2e8f0',
          boxShadow: isFocused
            ? '0 0 0 3.5px rgba(46,213,115,0.13), 0 2px 8px rgba(0,0,0,0.06)'
            : '0 1px 3px rgba(0,0,0,0.04)',
          transition: 'all 0.22s cubic-bezier(.4,0,.2,1)',
          // height prop → explicit height; padding adjusts to center content
          ...(height ? { height, minHeight: height, padding: '0 14px' } : {})
        }}
      >
        {/* ── Left icon ── */}
        <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {isLoading ? (
            /* Spinner arc */
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke={primaryColor}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ animation: '__spin 0.7s linear infinite' }}
            >
              <path d="M21 12a9 9 0 1 1-6.22-8.56" />
            </svg>
          ) : (
            /* Search glass */
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke={isFocused ? primaryColor : '#94a3b8'}
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ transition: 'stroke 0.2s ease' }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          )}
        </span>

        {/* ── Input ── */}
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          autoComplete="off"
          className="__search-input"
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: '0.875rem',
            color: '#1e293b',
            fontWeight: 500,
            lineHeight: 1.5,
            padding: 0,
            minWidth: 0
          }}
        />

        {/* ── Shortcut badge (hidden when typing) ── */}
        {!value && !isFocused && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
              fontSize: '0.68rem',
              color: '#94a3b8',
              background: '#eef0f3',
              border: '1px solid #dde1e7',
              borderRadius: '5px',
              padding: '1px 6px',
              fontFamily: 'monospace',
              letterSpacing: '0.04em',
              flexShrink: 0,
              userSelect: 'none'
            }}
          >
            ⌃K
          </span>
        )}

        {/* ── Clear button ── */}
        {value && (
          <button
            type="button"
            onClick={handleClear}
            tabIndex={-1}
            title="Clear"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: '#cbd5e1',
              border: 'none',
              cursor: 'pointer',
              color: '#fff',
              padding: 0,
              flexShrink: 0,
              transition: 'background 0.15s'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#94a3b8')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#cbd5e1')}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {/* ── Animated accent underline ── */}
      <div
        style={{ height: '2px', borderRadius: '0 0 6px 6px', overflow: 'hidden', marginTop: '1px' }}
      >
        <div
          style={{
            height: '100%',
            width: isFocused ? '100%' : '0%',
            background: `linear-gradient(90deg, ${primaryColor}, #00e676)`,
            transition: 'width 0.32s cubic-bezier(.4,0,.2,1)',
            borderRadius: '6px'
          }}
        />
      </div>
    </div>
  );
};

export default Search;
